import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showSuccess, showError } from '../../services/alert';

const inicial = {
  safraId: '',
  talhaoId: '',
  data: new Date().toISOString().substring(0, 10),
  produtoColhido: '',
  pesoBruto: '',
  pesoLiquido: '',
  umidade: '',
  impureza: '',
  quantidade: '',
  unidade: 'SC',
  armazem: 'Principal',
  valorMedioEstimado: ''
};

const ColheitaForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(inicial);
  const [loading, setLoading] = useState(false);
  const [safras, setSafras] = useState([]);
  const [talhoes, setTalhoes] = useState([]);

  const safraSelecionada = useMemo(
    () => safras.find(s => String(s.id) === String(form.safraId)),
    [safras, form.safraId]
  );

  useEffect(() => {
    const carregar = async () => {
      setLoading(true);
      try {
        const [safrasRes, talhoesRes] = await Promise.all([
          api.get('/safras'),
          api.get('/talhoes')
        ]);
        setSafras(Array.isArray(safrasRes.data) ? safrasRes.data : []);
        setTalhoes(Array.isArray(talhoesRes.data) ? talhoesRes.data : []);

        if (isEditing) {
          const res = await api.get(`/colheita/${id}`);
          setForm({
            safraId: res.data.safraId || '',
            talhaoId: res.data.talhaoId || '',
            data: res.data.data?.substring(0, 10) || inicial.data,
            produtoColhido: res.data.produtoColhido || '',
            pesoBruto: res.data.pesoBruto || '',
            pesoLiquido: res.data.pesoLiquido || '',
            umidade: res.data.umidade || '',
            impureza: res.data.impureza || '',
            quantidade: res.data.quantidade || '',
            unidade: res.data.unidade || 'SC',
            armazem: res.data.armazem || 'Principal',
            valorMedioEstimado: res.data.valorMedioEstimado || ''
          });
        }
      } catch {
        showError('Erro ao carregar dados da colheita.');
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [id, isEditing]);

  useEffect(() => {
    if (!isEditing && safraSelecionada?.talhaoId) {
      setForm(prev => ({
        ...prev,
        talhaoId: safraSelecionada.talhaoId,
        produtoColhido: prev.produtoColhido || safraSelecionada.cultura || ''
      }));
    }
  }, [isEditing, safraSelecionada]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      safraId: Number(form.safraId),
      talhaoId: Number(form.talhaoId),
      data: form.data,
      produtoColhido: form.produtoColhido,
      pesoBruto: Number(form.pesoBruto || 0),
      pesoLiquido: Number(form.pesoLiquido || 0),
      umidade: Number(form.umidade || 0),
      impureza: Number(form.impureza || 0),
      quantidade: Number(form.quantidade),
      unidade: form.unidade,
      armazem: form.armazem,
      valorMedioEstimado: Number(form.valorMedioEstimado || 0),
      produtoEstoqueId: null
    };

    try {
      if (isEditing) {
        await api.put(`/colheita/${id}`, payload);
        showSuccess('Colheita atualizada com sucesso.');
      } else {
        await api.post('/colheita', payload);
        showSuccess('Colheita criada e estoque agrícola atualizado.');
      }

      navigate('/colheitas');
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao salvar colheita.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate('/colheitas')}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>

          <h5 className="mb-0">{isEditing ? 'Editar Colheita' : 'Nova Colheita'}</h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Safra *</label>
              <select name="safraId" value={form.safraId} onChange={handleChange} className="form-select" required disabled={loading}>
                <option value="">Selecione...</option>
                {safras.map(s => (
                  <option key={s.id} value={s.id}>{s.cultura} - {s.areaHa} ha</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Talhão *</label>
              <select name="talhaoId" value={form.talhaoId} onChange={handleChange} className="form-select" required disabled={loading}>
                <option value="">Selecione...</option>
                {talhoes.map(t => (
                  <option key={t.id} value={t.id}>{t.nome} - {t.areaHa} ha</option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Data *</label>
              <input type="date" name="data" value={form.data} onChange={handleChange} className="form-control" required disabled={loading} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Produto colhido *</label>
              <input name="produtoColhido" value={form.produtoColhido} onChange={handleChange} className="form-control" placeholder="Soja, milho, algodão..." required disabled={loading} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Armazém *</label>
              <input name="armazem" value={form.armazem} onChange={handleChange} className="form-control" required disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Peso bruto</label>
              <input type="number" step="0.0001" name="pesoBruto" value={form.pesoBruto} onChange={handleChange} className="form-control" disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Peso líquido</label>
              <input type="number" step="0.0001" name="pesoLiquido" value={form.pesoLiquido} onChange={handleChange} className="form-control" disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Umidade %</label>
              <input type="number" step="0.0001" name="umidade" value={form.umidade} onChange={handleChange} className="form-control" disabled={loading} />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Impureza %</label>
              <input type="number" step="0.0001" name="impureza" value={form.impureza} onChange={handleChange} className="form-control" disabled={loading} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Quantidade final *</label>
              <input type="number" step="0.0001" name="quantidade" value={form.quantidade} onChange={handleChange} className="form-control" required disabled={loading} />
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Unidade *</label>
              <select name="unidade" value={form.unidade} onChange={handleChange} className="form-select" required disabled={loading}>
                <option value="SC">Sacas</option>
                <option value="KG">Kg</option>
                <option value="TON">Toneladas</option>
                <option value="UN">Unidade</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label fw-semibold">Valor médio estimado</label>
              <CurrencyInput name="valorMedioEstimado" value={form.valorMedioEstimado} onChange={handleChange} className="form-control" placeholder="0,00" disabled={loading} />
            </div>

            <div className="col-12 d-flex justify-content-end mt-3">
              <button type="submit" className="btn btn-success me-2 d-flex align-items-center gap-1" disabled={loading}>
                <i className="bi bi-save"></i>
                <span>{loading ? 'Salvando...' : 'Salvar'}</span>
              </button>

              <button type="button" className="btn btn-secondary d-flex align-items-center gap-1" onClick={() => navigate('/colheitas')} disabled={loading}>
                <i className="bi bi-x-circle"></i>
                <span>Cancelar</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ColheitaForm;
